//TODO: Create store to manage project exports
import { create } from "zustand";
import {
  BuildJobListInfo,
  BuildJobListInfoSchema,
  BuildJobStatusSchema,
  BuildTypeSchema,
} from "../../darvin-shared/schema/zod/build";
import { BuildApi } from "../api/buildApi";
import { useProjectStore } from "./useProjectStore";

interface ProjectExportsActions {
  initialize: () => Promise<void>;
  startExport: () => Promise<void>;
  processingExportUpdated: (exportJob: BuildJobListInfo) => void;
  processingExportFinished: () => Promise<void>;
  loadExportUrl: () => Promise<string | null>;
  dispose: () => void;
}

export enum ExportStatus {
  EXPORTING = "exporting",
  DONE = "done",
  ERROR = "error",
}

interface ProjectExportsState {
  latestExport: BuildJobListInfo | null;
  latestCompleteExport: BuildJobListInfo | null;
  exportStatus: ExportStatus | null;

  actions: ProjectExportsActions;
}

export const useProjectExportsStore = create<ProjectExportsState>(
  (set, get) => {
    return {
      latestExport: null,
      latestCompleteExport: null,
      exportStatus: null,

      actions: {
        initialize: async () => {
          const projectId = useProjectStore.getState().projectId;

          if (projectId) {
            const latestExportResponse = await BuildApi.loadBuilds({
              projectId,
              buildTypes: [BuildTypeSchema.Enum.zip],
              limit: 1,
            });
            const latestCompleteExportResponse = await BuildApi.loadBuilds({
              projectId,
              buildTypes: [BuildTypeSchema.Enum.zip],
              statuses: [BuildJobStatusSchema.Enum.complete],
              limit: 1,
            });

            let latestExport = null;
            let latestCompleteExport = null;
            let exportStatus = null;

            if (latestExportResponse.builds.length > 0) {
              latestExport = latestExportResponse.builds[0];
              if (
                latestExport.status === BuildJobStatusSchema.Enum.waiting ||
                latestExport.status === BuildJobStatusSchema.Enum.running
              ) {
                exportStatus = ExportStatus.EXPORTING;
              }
            }
            if (latestCompleteExportResponse.builds.length > 0) {
              latestCompleteExport = latestCompleteExportResponse.builds[0];
            }

            set({
              latestExport,
              latestCompleteExport,
              exportStatus,
            });
          }
        },

        startExport: async () => {
          const projectId = useProjectStore.getState().projectId;
          if (projectId) {
            const startBuildResponse = await BuildApi.startBuild(
              projectId,
              BuildTypeSchema.Enum.zip
            );
            const latestExportResponse = await BuildApi.loadSingleBuild(
              projectId,
              startBuildResponse.buildId
            );
            set({
              latestExport: BuildJobListInfoSchema.parse(
                latestExportResponse.buildJob
              ),
              exportStatus: ExportStatus.EXPORTING,
            });
          }
        },

        processingExportUpdated: (exportJob: BuildJobListInfo) => {
          const latestExport = get().latestExport;
          if (
            latestExport &&
            latestExport.buildId === exportJob.buildId &&
            latestExport.status !== exportJob.status
          ) {
            set({
              latestExport: exportJob,
            });
          }
        },

        processingExportFinished: async (): Promise<void> => {
          if (get().exportStatus !== ExportStatus.EXPORTING) {
            return;
          }
          const projectId = useProjectStore.getState().projectId;
          if (projectId) {
            const exportJob = await BuildApi.loadBuilds({
              projectId,
              buildTypes: [BuildTypeSchema.Enum.zip],
              limit: 1,
            });
            if (exportJob.builds.length > 0) {
              const updatedLatestExport = exportJob.builds[0];
              let latestExport = null;
              let latestCompleteExport = null;
              if (
                updatedLatestExport.status ===
                BuildJobStatusSchema.Enum.complete
              ) {
                latestCompleteExport = updatedLatestExport;
              } else {
                latestExport = updatedLatestExport;
                latestCompleteExport = get().latestCompleteExport;
              }
              let exportStatus = get().exportStatus;
              if (
                updatedLatestExport.status ===
                BuildJobStatusSchema.Enum.complete
              ) {
                exportStatus = ExportStatus.DONE;
              } else if (
                updatedLatestExport.status ===
                  BuildJobStatusSchema.Enum.error ||
                updatedLatestExport.status ===
                  BuildJobStatusSchema.Enum.cancelled
              ) {
                exportStatus = ExportStatus.ERROR;
              }
              set({
                latestExport,
                latestCompleteExport,
                exportStatus,
              });
              if (
                exportStatus === ExportStatus.DONE ||
                exportStatus === ExportStatus.ERROR
              ) {
                // To avoid showing snackbar again
                setTimeout(() => {
                  set({
                    exportStatus: null,
                  });
                }, 500);
              }
            }
          }
        },

        loadExportUrl: async (): Promise<string | null> => {
          const projectId = useProjectStore.getState().projectId;
          const exportId = get().latestCompleteExport?.buildId;
          return projectId && exportId
            ? await BuildApi.loadUrl(projectId, exportId)
            : null;
        },

        dispose: () => {
          set({
            latestExport: null,
            latestCompleteExport: null,
            exportStatus: null,
          });
        },
      },
    };
  }
);

export const useProjectLatestExport = () =>
  useProjectExportsStore((state) => state.latestExport);

export const useProjectLatestCompleteExport = () =>
  useProjectExportsStore((state) => state.latestCompleteExport);

export const useProjectExportStatus = () =>
  useProjectExportsStore((state) => state.exportStatus);

export const useProjectExportsActions = () =>
  useProjectExportsStore((state) => state.actions);
